// Opsjonsmatematikk som deles mellom fanene.
// Kontrakter har formen { type: "C"|"P", strike, expiration: "YYYY-MM-DD", volume, openInterest, impliedVolatility, bid, ask, last }.

// Black-Scholes gamma: φ(d1) / (S × σ × √T)
export function bsGamma(S, K, T, sigma, r = 0.05) {
  if (T <= 0 || sigma <= 0 || S <= 0 || K <= 0) return 0;
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * sqrtT);
  return Math.exp(-d1 * d1 / 2) / (Math.sqrt(2 * Math.PI) * S * sigma * sqrtT);
}

const daysTo = (exp, now = Date.now()) => (new Date(exp + "T16:00:00") - now) / 86400000;

// Dealer-gamma (vanlig GEX-konvensjon: kundene kjøper calls og puts, dealerne er motpart).
// netGex > 0: dealerne demper bevegelser. netGex < 0: de forsterker dem.
// zeroGamma: kursen der samlet gamma skifter fortegn (regnet på nytt for hver tenkt kurs).
export function gammaProfile(contracts, cp, fallbackIV = null) {
  const C = contracts
    .filter(o => o.expiration && (o.openInterest || 0) > 0 && o.strike > cp * 0.7 && o.strike < cp * 1.3)
    .map(o => ({
      K: o.strike, oi: o.openInterest, sign: o.type === "C" ? 1 : -1,
      T: Math.max(1, Math.round(daysTo(o.expiration))) / 365,
      iv: o.impliedVolatility > 0 ? o.impliedVolatility : fallbackIV,
    }))
    .filter(o => o.iv > 0);
  if (!C.length) return null;
  const at = S => C.reduce((s, o) => s + o.sign * o.oi * bsGamma(S, o.K, o.T, o.iv) * S * S * 0.01, 0);
  const netGex = at(cp);
  let zeroGamma = null;
  const grid = Array.from({ length: 61 }, (_, k) => cp * (1 + (k - 30) * 0.005));
  const vals = grid.map(at);
  for (let i = 1; i < grid.length; i++) {
    if ((vals[i - 1] <= 0) === (vals[i] <= 0)) continue;
    const S = grid[i - 1] + (grid[i] - grid[i - 1]) * (-vals[i - 1]) / (vals[i] - vals[i - 1]);
    if (zeroGamma == null || Math.abs(S - cp) < Math.abs(zeroGamma - cp)) zeroGamma = S;
  }
  return { netGex, positive: netGex >= 0, zeroGamma };
}

// Opsjonsvegger: strike med størst call-OI over kursen (motstand) og størst put-OI under (støtte),
// utløp innen maxDays og strike innen ±15 % (langt ute er det mest gamle sikringer, ikke magneter).
export function walls(contracts, cp, maxDays = 35) {
  const callOI = {}, putOI = {};
  for (const o of contracts) {
    if (!o.expiration || daysTo(o.expiration) > maxDays || daysTo(o.expiration) < -0.5) continue;
    if (Math.abs(o.strike - cp) / cp > 0.15) continue;
    if (o.type === "C" && o.strike > cp) callOI[o.strike] = (callOI[o.strike] || 0) + (o.openInterest || 0);
    if (o.type === "P" && o.strike < cp) putOI[o.strike] = (putOI[o.strike] || 0) + (o.openInterest || 0);
  }
  const top = m => { const e = Object.entries(m).sort((a, b) => b[1] - a[1])[0]; return e ? { strike: +e[0], oi: e[1] } : null; };
  return { callWall: top(callOI), putWall: top(putOI) };
}

// Dagens flyt: premie = volum × midtpris × 100. Vi ser volum, ikke om det var kjøp eller salg.
// Utløp innen minDays holdes utenfor: 0–1 DTE er dagshandel og drukner posisjoneringen.
export function flowSummary(contracts, cp, minDays = 2) {
  let callPrem = 0, putPrem = 0, callVol = 0, putVol = 0;
  const unusual = [];
  for (const c of contracts) {
    const v = c.volume || 0;
    if (!v || !c.expiration || daysTo(c.expiration) < minDays) continue;
    const bidAsk = (c.bid || 0) + (c.ask || 0);
    const mid = bidAsk > 0 ? bidAsk / 2 : (c.last || 0);
    const prem = v * mid * 100;
    if (c.type === "C") { callPrem += prem; callVol += v; } else { putPrem += prem; putVol += v; }
    if (v >= 100 && v > (c.openInterest || 0) && prem >= 50000) {
      const otm = c.type === "C" ? (c.strike - cp) / cp * 100 : (cp - c.strike) / cp * 100;
      unusual.push({ ...c, prem, ratio: v / Math.max(1, c.openInterest || 0), otm });
    }
  }
  unusual.sort((a, b) => b.prem - a.prem);
  const tot = callPrem + putPrem;
  return {
    callPrem, putPrem, callVol, putVol,
    pcVol: callVol ? putVol / callVol : null,
    callShare: tot > 0 ? callPrem / tot : null,
    unusual: unusual.slice(0, 8),
  };
}
